import { useHudStore } from "../store/hudStore";
import { DOMAINS, heatColor } from "../data/seed";
import { restsToday } from "../lib/quests";
import { completedTodayIn, xpEarnedToday } from "../store/selectors";
import Page from "../components/Page";
import EngineerSprite, { type EngineerVariant } from "../components/EngineerSprite";
import CompactRow from "../components/CompactRow";
import AddQuestForm from "../components/AddQuestForm";
import { Button, Card, Stat } from "../design-system/components";
import HealthModule from "../components/modules/HealthModule";
import PeopleModule from "../components/modules/PeopleModule";
import FinanceModule from "../components/modules/FinanceModule";
import CertsModule from "../components/modules/CertsModule";
import GamesModule from "../components/modules/GamesModule";

// Domain page (app.html screenDomain, 836–860). Mirrors the original's empty=true;
// the special module (finance/people/health) shows only when live data exists.
export default function Domain() {
  const st = useHudStore();
  const empty = !st.live;
  const d = DOMAINS.find((x) => x.name === st.domain) || DOMAINS[0];
  if (!d) return null;
  // A weekday-only daily (the day job) rests on the Manila weekend: it is not
  // open work today, so the domain's open list leaves it out until Monday.
  const quests = empty ? [] : st.quests.filter((q) => q.domain === d.name && !st.done[q.id] && !restsToday(q));
  const color = empty ? "var(--mut)" : heatColor(d.health);
  // The tile says "XP today", so it counts XP banked today, not XP left to earn.
  const xpToday = xpEarnedToday(st, d.name);
  // Today's completions show under the open list. Without them the page could
  // read "XP today 100" over "Nothing open here. This domain is clear", which
  // states a fact and then hides the only evidence for it.
  const clearedToday = empty ? [] : completedTodayIn(st, d.name);

  const financeLive = d.name === "Finances" && st.live;
  const peopleLive = d.name === "Relationships" && st.live && !!st.people.length;
  const healthLive = d.name === "Health" && st.live && !!(st.moodWeek.some((m) => m.mood) || st.bodyWeek.some((b) => b.done) || st.mindCare.length);
  const certsLive = d.name === "Learning" && st.live && !!st.certs.length;
  const gamesLive = d.name === "Entertainment" && st.live && !!st.games.length;
  const moduleEl = financeLive ? <FinanceModule /> : peopleLive ? <PeopleModule /> : healthLive ? <HealthModule /> : certsLive ? <CertsModule /> : gamesLive ? <GamesModule /> : null;

  return (
    <Page back="All domains">
      <Card className="detail-hero domain-hero">
        <span className="detail-identity-icon" style={{ color }}><i aria-hidden="true" className={d.icon} /></span>
        <span className="domain-hero-engineer" aria-hidden="true"><EngineerSprite variant={d.sprite as EngineerVariant} /></span>
        <div className="detail-head-body"><h1 className="detail-heading detail-title">{d.name}</h1><p className="detail-copy">{d.sub}</p></div>
        <div className="domain-stats"><Stat label="XP today" value={empty ? 0 : xpToday} tone="accent" /><Stat label="Health" value={(empty ? 4 : d.health) + "%"} tone="accent" /><Stat label="Last touched" value={empty ? "never" : d.last} /></div>
      </Card>
      {moduleEl}
      <Card title="Quests in this domain" actions={<Button size="compact" onClick={() => st.adding ? st.cancelAdd() : st.openAddHere(d.name)} icon={st.adding ? "fa-solid fa-xmark" : "fa-solid fa-plus"}>{st.adding ? "Close" : "Add here"}</Button>}>
        {st.adding && <AddQuestForm />}
        {quests.map((q) => <CompactRow key={q.id} quest={q} showDomainChip={false} />)}
        {clearedToday.map((q) => <CompactRow key={q.id} quest={q} showDomainChip={false} />)}
        {quests.length === 0 && clearedToday.length === 0 && !st.adding && <p className="detail-empty">Nothing open here. This domain is clear. Add a quest to keep it warm.</p>}
        {quests.length === 0 && clearedToday.length > 0 && <p className="detail-empty">Nothing open here. Add a quest to keep it warm.</p>}
      </Card>
    </Page>
  );
}
